import { MimeTypeEnum } from '@/graphql/types';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Menu, MenuItem } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { toast } from 'react-toastify';
import CanvasEditor from './canvas';
import type { CanvasMediaItem } from './canvas/props';
import { useCanvasStore } from './canvas/store/canvasStore';
import type { AnimationType } from './canvas/types';
import {
  type CanvasMediaItemInput,
  isCanvasThumbnail,
  loadCanvasStateFromMedia,
  saveCanvasAsMedia,
} from './canvas/utils/save';
import MediaPickerModalButton from './inputs/MediaPicker';

type MediaPickerFile = {
  readonly author?:
    | {
        readonly node?: {
          readonly id: string;
          readonly name: string | null | undefined;
          readonly slug: string | null | undefined;
          readonly username: string | null | undefined;
        };
      }
    | null
    | undefined;
  readonly description?: string | null | undefined;
  readonly mediaItemUrl?: string | null | undefined;
  readonly id?: string | undefined;
  readonly title?: string | null | undefined;
} | null;

interface CanvasEditorComponentProps {
  onClose?: () => void;
  onSave?: (mediaItem: CanvasMediaItem) => void;
  editItem?: CanvasMediaItem;
  mode?: 'create' | 'edit';
}

const convertToCompatibleFormat = (file: MediaPickerFile | CanvasMediaItem): CanvasMediaItemInput => ({
  id: file?.id,
  title: file?.title || '',
  mediaItemUrl: file?.mediaItemUrl || undefined,
  description: file?.description || undefined,
});

const resetCanvasToDefault = () => {
  useCanvasStore.getState().resetCanvas();

  useCanvasStore.setState({
    texts: [],
    backgroundColor: '#ffffff',
    bgImage: null,
    logoImage: null,
    logoProps: {
      x: 300,
      y: 305,
      width: 200,
      height: 80,
      animation: 'none',
    },
    selectedId: null,
    isEditingTextId: null,
  });
};

const CanvasEditorComponent: React.FC<CanvasEditorComponentProps> = ({
  onClose,
  onSave,
  editItem,
  mode = 'create',
}) => {
  const [title, setTitle] = useState(editItem?.title || 'Canvas Thumbnail');
  const [isSaving, setIsSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFormat, setSelectedFormat] = useState<string>(editItem?.format || 'png');

  const setBgImageRef = useRef(useCanvasStore((s) => s.setBgImage));

  const hasTextAnimations = useCanvasStore((state) => state.texts.some((t) => t.animation && t.animation !== 'none'));

  const hasLogoAnimation = useCanvasStore((state) => state.logoProps.animation && state.logoProps.animation !== 'none');

  const hasImageAnimations = useCanvasStore((state) =>
    state.canvasImages.some((img) => img.animation && img.animation !== 'none' && !img.deleted)
  );

  const hasAnimations = useMemo(
    () => hasTextAnimations || hasLogoAnimation || hasImageAnimations,
    [hasTextAnimations, hasLogoAnimation, hasImageAnimations]
  );

  const verifyCanvasStateLoaded = useCallback(() => {
    const state = useCanvasStore.getState();
    return state.texts.length > 0 || state.bgImage !== null || state.logoImage !== null;
  }, []);

  const editItemRef = useRef(editItem);

  useEffect(() => {
    if (mode === 'edit' && editItemRef.current?.mediaItemUrl) {
      resetCanvasToDefault();
      const compatibleItem = convertToCompatibleFormat(editItemRef.current);

      const stateLoaded = loadCanvasStateFromMedia(compatibleItem);
      const hasValidState = verifyCanvasStateLoaded();

      if (!hasValidState) {
        setBgImageRef.current(editItemRef.current.mediaItemUrl);
        useCanvasStore.setState({ texts: [] });
      }
    }
  }, []);

  const handleModalClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && onClose) {
        onClose();
      }
    },
    [onClose]
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFormatSelect = (format: string) => {
    setSelectedFormat(format);
    handleMenuClose();
  };

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const success = await saveCanvasAsMedia(title as string, selectedFormat, editItem?.id);

      if (success) {
        if (onSave) {
          onSave({
            title,
            format: selectedFormat,
            id: editItem?.id,
            mediaItemUrl: editItem?.mediaItemUrl,
          });
        }

        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      // Empty catch block
    } finally {
      setIsSaving(false);
    }
  }, [title, selectedFormat, onSave, onClose, isSaving, editItem]);

  const handleThumbnailSelected = useCallback((file: MediaPickerFile): void => {
    if (!file?.mediaItemUrl || !file?.id) return;

    resetCanvasToDefault();
    const titleSuggestsCanvas = file.title?.toLowerCase?.()?.includes('canvas thumbnail');
    const compatibleFile = convertToCompatibleFormat(file);

    try {
      const isCanvasThumb = isCanvasThumbnail(compatibleFile);
      const stateLoaded = loadCanvasStateFromMedia(compatibleFile);

      setTitle(file.title || 'Edited Thumbnail');

      if (stateLoaded || titleSuggestsCanvas) {
        if (stateLoaded) {
          toast.success('Canvas thumbnail state loaded for editing');
        } else if (titleSuggestsCanvas) {
          setBgImageRef.current(file.mediaItemUrl);

          const textId = `text-${Date.now()}`;
          const defaultText = {
            id: textId,
            text: 'Edit this Canvas Thumbnail',
            x: 200,
            y: 200,
            width: 400,
            color: '#ffffff',
            fontFamily: 'Arial',
            fontSize: 32,
            lineHeight: 1.5,
            letterSpacing: 0,
            align: 'center' as const,
            animation: 'none' as AnimationType,
            animationDuration: 1,
            animationDelay: 0,
          };

          useCanvasStore.setState({ texts: [defaultText] });
          toast.info('Canvas thumbnail loaded for editing. Add your text or logo.');
        }
      } else {
        setBgImageRef.current(file.mediaItemUrl);
        useCanvasStore.setState({ texts: [] });
        toast.info('Image loaded as background. Add text or logo to create a thumbnail.');
      }
    } catch (error) {
      toast.error('Failed to load thumbnail. Using as background image.');
      setBgImageRef.current(file.mediaItemUrl);
      useCanvasStore.setState({ texts: [] });
    }
  }, []);
  return (
    <div className="canvas-modal canvas-editor-component" onClick={handleModalClick}>
      <div className="canvas-modal-backdrop" />
      <div className="canvas-modal-content">
        <Box
          sx={{
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--zngly-border)',
            backgroundColor: 'white',
          }}
        >
          <div style={{ width: '300px' }}>
            <label
              htmlFor="thumbnail-title"
              style={{
                display: 'block',
                fontSize: '12px',
                marginBottom: '4px',
                color: '#666',
              }}
            >
              Thumbnail Title
            </label>
            <input
              id="thumbnail-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isSaving}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </div>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MediaPickerModalButton
              buttonTitle="Edit Existing"
              buttonIcon={<EditIcon />}
              mimeTypes={[MimeTypeEnum.ImageJpeg, MimeTypeEnum.ImagePng, MimeTypeEnum.ImageGif, MimeTypeEnum.ImageWebp]}
              onFileSelect={handleThumbnailSelected}
              showThumbnailButton={false}
              disabled={isSaving}
            />

            <Box sx={{ position: 'relative', display: 'flex', marginLeft: 1 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={isSaving}
                onClick={handleSave}
                sx={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  paddingRight: 2,
                }}
              >
                {isSaving ? 'Saving...' : `${mode === 'edit' ? 'Update' : 'Save as'} ${selectedFormat.toUpperCase()}`}
              </Button>

              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleMenuClick}
                sx={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeft: '1px solid rgba(255,255,255,0.3)',
                  minWidth: '36px',
                  paddingLeft: 1,
                  paddingRight: 1,
                }}
                disabled={isSaving}
              >
                <ArrowDropDownIcon />
              </Button>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{
                zIndex: 9999,
                '& .MuiPaper-root': {
                  minWidth: '200px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  border: '1px solid #eee',
                },
              }}
            >
              <MenuItem onClick={() => handleFormatSelect('png')} selected={selectedFormat === 'png'} sx={{ py: 1.5 }}>
                PNG (High Quality)
              </MenuItem>
              <MenuItem
                onClick={() => handleFormatSelect('jpeg')}
                selected={selectedFormat === 'jpeg'}
                sx={{ py: 1.5 }}
              >
                JPEG (Smaller Size)
              </MenuItem>
              <MenuItem
                onClick={() => handleFormatSelect('webp')}
                selected={selectedFormat === 'webp'}
                sx={{ py: 1.5 }}
              >
                WebP (Best Compression)
              </MenuItem>
              <MenuItem
                onClick={() => handleFormatSelect('gif')}
                selected={selectedFormat === 'gif'}
                disabled={!hasAnimations}
                sx={{
                  py: 1.5,
                  opacity: hasAnimations ? 1 : 0.5,
                }}
              >
                GIF (Animated - 3s)
              </MenuItem>
            </Menu>

            {onClose && (
              <IconButton onClick={onClose} size="small" sx={{ ml: 2 }} disabled={isSaving}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <CanvasEditor onSave={handleSave} hideCreateThumbnail={true} />
      </div>
    </div>
  );
};

export default CanvasEditorComponent;
